/**
 * Run identity.
 *
 * A run's seed is its identity. The generator is a disposable function derived
 * from that identity — never the other way round. Restarting re-derives it;
 * it does not preserve whatever stream happened to survive the last fight.
 */
export const RUN_VERSION = '0.2.0';

/**
 * Crockford-ish base32. No I, O, 0 or 1 — nothing that reads wrong when a
 * player is copying a code off a screen.
 */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** `G7K4-92PM` — something a player can read out loud without ambiguity. */
export function encodeSeed(seed: number): string {
  let n = seed >>> 0;
  let out = '';
  for (let i = 0; i < 8; i++) {
    out += ALPHABET[n % 32];
    n = Math.floor(n / 32);
  }
  return `${out.slice(0, 4)}-${out.slice(4)}`;
}

/** Accepts the display code, or a bare integer. Null when unparseable. */
export function decodeSeed(raw: string): number | null {
  const clean = raw.trim();
  if (clean.length === 0) return null;

  // A plain number is the most common case in a URL. `Number` handles both
  // decimal and 0x-prefixed hex natively.
  if (/^-?\d+$/.test(clean) || /^0x[0-9a-f]+$/i.test(clean)) {
    const n = Number(clean);
    return Number.isFinite(n) ? n >>> 0 : null;
  }

  const code = clean.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (code.length !== 8) return null;
  let n = 0;
  for (let i = code.length - 1; i >= 0; i--) {
    const idx = ALPHABET.indexOf(code[i] ?? '');
    if (idx < 0) return null;
    n = n * 32 + idx;
  }
  return n >>> 0;
}

/** A fresh seed. Time and entropy, so two runs in the same millisecond differ. */
export function generateSeed(): number {
  return (Date.now() ^ (Math.floor(Math.random() * 0xffffffff))) >>> 0;
}

/**
 * Read the seed from `?seed=`, accepting either form. Development and share
 * links use this; a start-screen seed box can come later.
 */
export function seedFromUrl(search: string): number | null {
  try {
    const params = new URLSearchParams(search);
    const raw = params.get('seed');
    return raw === null ? null : decodeSeed(raw);
  } catch {
    return null;
  }
}